'use client';

import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  mobileGraphql,
  mobileKeys,
  type MobileConnection,
  type MobileFrontEndBubbleMessageView,
  type MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import {
  buildThreadMessagesQuery,
  buildThreadWindowQuery,
  messageFieldSelection,
  messageRootQuery,
} from '../lib/build-message-query';
import { ARTIFACT_ID_FIELD } from '../lib/constants';
import { timestampMillis } from '../lib/timestamps';
import {
  isCompositeThreadKey,
  mapMessage,
  resolveThreadKey,
  type MappedMessage,
} from '../lib/message-mapping';

/** Rows scanned when a thread key is derived client-side (no server-side filter column). */
const COMPOSITE_WINDOW_LIMIT = 1000;

/** Upper bound on messages fetched for a single thread (threads are bounded). */
const THREAD_MESSAGE_LIMIT = 1000;

/** Messages sort oldest-first, so unknown timestamps sort last (POSITIVE_INFINITY). */
const MISSING_TIMESTAMP = Number.POSITIVE_INFINITY;

/** Int/BigInt GraphQL scalars that must not be sent as quoted string literals. */
const INT_GQL_TYPES = new Set(['Int', 'BigInt']);

/**
 * Coerce a thread-key string to the GraphQL scalar expected by ``where.{field}.equals``.
 * Falls back to the raw string when the field type is unknown or non-numeric.
 */
function coerceThreadWhereValue(
  field: string | undefined,
  value: string,
  artifact: MobileUiConfigArtifact,
): string | number {
  if (!field) return value;
  const spec = artifact.fields.find((f) => f.name === field);
  if (spec && INT_GQL_TYPES.has(spec.type)) {
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }
  // by_parent parent ids and mobileThreadId are Int even if ui-config fields lag.
  if (field === 'mobileThreadId' || field.endsWith('ArtifactId')) {
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }
  return value;
}

interface UseThreadMessagesArgs {
  mobileAcquisitionId: number;
  view: MobileFrontEndBubbleMessageView;
  artifact: MobileUiConfigArtifact;
  detailFields: string[];
  /** Selected thread key (parent artifact id or thread-field value). Null ⇒ idle. */
  threadKey: string | null;
  /** When false the query stays idle. Defaults to on. */
  active?: boolean;
}

/**
 * Fetch and order (oldest → newest) the messages for one selected thread.
 *
 * ``by_field`` filters the message list on ``thread.key_field``; ``by_parent``
 * filters on ``thread.parent_link_field`` (e.g. ``chatArtifactId``). Ordering is
 * done client-side by ``timestamp_field`` so it works even for message types
 * without a server-side ``orderBy``.
 */
export function useThreadMessages(
  args: UseThreadMessagesArgs,
): UseQueryResult<MappedMessage[], Error> {
  const { cellId, accessToken, enabled } = useCellServiceSession();
  const { mobileAcquisitionId, view, artifact, detailFields, threadKey } = args;

  const rootQuery = messageRootQuery(artifact);
  const composite = isCompositeThreadKey(view);
  const whereField =
    view.thread.mode === 'by_field' ? view.thread.key_field : view.thread.parent_link_field;
  // GraphQL ``equals`` must match the field's scalar type. Parent links and
  // ``mobileThreadId`` are Int; vendor thread keys (chatId, conversationId) stay
  // strings. Coerce from the introspected ui-config field type when available.
  const whereValue =
    threadKey != null && threadKey !== ''
      ? coerceThreadWhereValue(whereField, threadKey, artifact)
      : threadKey;

  const selection = messageFieldSelection(view, detailFields);

  return useQuery<MappedMessage[], Error>({
    queryKey: mobileKeys.threadMessages(
      cellId,
      mobileAcquisitionId,
      artifact.graphql_type,
      threadKey ?? '',
      { mode: view.thread.mode },
    ),
    enabled:
      enabled &&
      args.active !== false &&
      Boolean(rootQuery) &&
      threadKey != null &&
      threadKey !== '' &&
      (composite ||
        (Boolean(whereField) &&
          whereValue !== undefined &&
          !(typeof whereValue === 'number' && Number.isNaN(whereValue)))),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      // Composite keys have no server column to filter on, so scan a window and
      // match the derived key client-side.
      const query = composite
        ? buildThreadWindowQuery({
            rootQuery: rootQuery as string,
            mobileAcquisitionId,
            offset: 0,
            limit: COMPOSITE_WINDOW_LIMIT,
            selection,
          })
        : buildThreadMessagesQuery({
            rootQuery: rootQuery as string,
            mobileAcquisitionId,
            offset: 0,
            limit: THREAD_MESSAGE_LIMIT,
            whereField: whereField as string,
            whereValue: whereValue as string | number,
            selection,
          });
      if (!query) throw new Error('Invalid thread root query');
      const data = await mobileGraphql<Record<string, MobileConnection>>({
        cellId: cellId as string,
        accessToken: accessToken as string,
        endpoint: artifact.endpoint,
        query,
        signal,
      });
      const connection = data[rootQuery as string];
      if (!connection) throw new Error(`Missing "${rootQuery}" in mobile-service response`);
      const rows = composite
        ? connection.items.filter((row) => resolveThreadKey(row, view) === threadKey)
        : connection.items;
      return rows
        .map((row) => mapMessage(row, view, ARTIFACT_ID_FIELD))
        .sort(
          (a, b) =>
            timestampMillis(a.timestamp, MISSING_TIMESTAMP) -
            timestampMillis(b.timestamp, MISSING_TIMESTAMP),
        );
    },
  });
}
